import type { FlagName, Permission } from '@crm/contracts';
import { ShieldOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { useSession } from '../../lib/session.tsx';

/**
 * Page-level mirror of the API's `config: { permission, feature }`: the navigation hides a
 * page the user cannot use, and this keeps a typed-in URL honest too, with a reason.
 */
export function Gate({
  permission,
  feature = 'always',
  children,
}: {
  permission: Permission;
  feature?: FlagName | 'always';
  children: ReactNode;
}) {
  const session = useSession();
  if (!session.can(permission)) {
    return (
      <Blocked
        reason={`You do not have the ${permission} permission in ${session.me.organisation.name}.`}
      />
    );
  }
  if (feature !== 'always' && !session.isEnabled(feature)) {
    return (
      <Blocked reason={`This area is behind the ${feature} feature flag, which is off for you.`} />
    );
  }
  return <>{children}</>;
}

function Blocked({ reason }: { reason: string }) {
  return (
    <div
      className="bg-card border-border rounded-card shadow-card mx-auto mt-12 max-w-md border px-6 py-10 text-center"
      role="alert"
      data-testid="gate-blocked"
    >
      <span className="bg-muted text-muted-foreground mx-auto grid size-12 place-items-center rounded-2xl">
        <ShieldOff className="size-6" />
      </span>
      <p className="mt-4 font-semibold">Not available</p>
      <p className="text-muted-foreground mt-1 text-sm">{reason}</p>
    </div>
  );
}
